import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './alerts.css';
import { Alert } from './AlertBody';
import { ReactComponent as IconCancel } from '../../assets/images/icons/ico_cancel.svg';
import { ReactComponent as IconAceptar } from '../../assets/images/icons/ico_aceptar.svg';
import { ReactComponent as IconDelete } from '../../assets/images/icons/ico_delete.svg';
import React from 'react';
const classes = {
	confirm: {
		container: 'swal-container',
		popup: 'swal-popup',
		title: 'swal-title',
		confirmButton: 'seia-button btn-accept',
		denyButton: 'seia-button btn-deny',
		cancelButton: 'seia-button btn-cancel',
		actions: 'sweetalert-react-actions'
	},
	danger: {
		container: 'swal-container',
		popup: 'swal-popup',
		title: 'swal-title',
		confirmButton: 'seia-button btn-danger',
		denyButton: 'seia-button btn-deny',
		cancelButton: 'seia-button btn-cancel',
		actions: 'sweetalert-react-actions'
	},
	error: {
		container: 'swal-container',
		popup: 'swal-popup',
		title: 'swal-title',
		confirmButton: 'seia-button btn-danger',
		denyButton: 'seia-button btn-deny',
		cancelButton: 'seia-button btn-cancel',
		actions: 'sweetalert-react-actions'
	}
};

/**
 * Mostrar alerta
 * @param {Object} props - Propiedades del componente Alert.
 * @param {string} props.title - Título de la alerta.
 * @param {string} props.subtitle - Subtítulo de la alerta.
 * @param {string} props.message - Mensaje complementario de la alerta.
 * @param {string} props.type - success | error | warning - Tipo de ícono en la alerta.
 * @param {string} props.confirmButtonText - Texto para botón confirmar.
 * @param {Function} confirmCallback - Función ejecutada al confirmar.
 * @param {Function} cancelCallback - Función ejecutada al cancelar.
 * @param {*} swalProps - Propiedades para el componente de SweetAlerts.
 */

const AlertActionButton = ({ icon, label }) => {
	return (
		<div className="sweetalert-react-button">
			{icon && icon}
			<span className="align-middle mx-auto icon-button-span">{label}</span>
		</div>
	);
};

const CancelActionButton = ({ label, ...other }) => {
	return (
		<AlertActionButton icon={<IconCancel />} label={label || 'Cancelar'} />
	);
};

const ConfirmActionButton = ({ label, ...other }) => {
	return (
		<AlertActionButton icon={<IconAceptar />} label={label || 'Aceptar'} />
	);
};

const DeleteActionButton = ({ label, ...other }) => {
	return (
		<AlertActionButton icon={<IconDelete />} label={label || 'Eliminar'} />
	);
};

const baseAlert = async (
	props,
	confirmCallback,
	cancelCallback,
	confirmButton,
	cancelButton,
	swalProps,
	type
) => {
	const swal = withReactContent(Swal);
	const result = await swal.fire({
		allowOutsideClick: false,
		showCloseButton: false,
		showCancelButton: true,
		reverseButtons: true,
		customClass: {
			...classes[type]
		},
		confirmButtonText: confirmButton,
		cancelButtonText: cancelButton,
		html: <Alert {...props} />,
		...swalProps
	});

	if (result.isConfirmed) {
		if (confirmCallback) {
			confirmCallback();
		}
	}

	if (result.isDenied || result.isDismissed) {
		if (cancelCallback) {
			cancelCallback();
		}
	}
	return result;
};

const confirmationAlert = async (
	props,
	confirmCallback,
	cancelCallback,
	swalProps
) => {
	return baseAlert(
		props,
		confirmCallback,
		cancelCallback,
		<ConfirmActionButton label="Aceptar" />,
		<CancelActionButton label="Cancelar" />,
		swalProps,
		'confirm'
	);
};

const alert = async (props, confirmCallback, cancelCallback, swalProps) => {
	const successSwal = withReactContent(Swal);

	const result = await successSwal.fire({
		allowOutsideClick: false,
		showCloseButton: false,
		customClass: {
			...classes['confirm']
		},
		confirmButtonText: (
			<div className="sweetalert-react-button">
				<IconAceptar />
				<span className="align-middle mx-auto icon-button-span">Aceptar</span>
			</div>
		),
		cancelButtonText: (
			<div className="sweetalert-react-button">
				<IconCancel />
				<span className="align-middle mx-auto icon-button-span">Cancelar</span>
			</div>
		),
		html: <Alert {...props} />,
		...swalProps
	});

	if (result.isConfirmed) {
		if (confirmCallback) {
			confirmCallback();
		}
	}
	if (result.isDenied || result.isDismissed) {
		if (cancelCallback) {
			cancelCallback();
		}
	}
	return result;
};

const deleteAlert = async (
	props,
	confirmCallback,
	cancelCallback,
	swalProps
) => {
	return baseAlert(
		props,
		confirmCallback,
		cancelCallback,
		<DeleteActionButton label={props.confirmButtonText} />,
		<CancelActionButton label="Cancelar" />,
		swalProps,
		'danger'
	);
};

const errorAlert = async (props, confirmCallback, cancelCallback) => {
	return baseAlert(
		props,
		confirmCallback,
		cancelCallback,
		<CancelActionButton label="Aceptar" />,
		!!cancelCallback,
		{ showCancelButton: !!cancelCallback },
		'error'
	);
};

const successAlert = async (props, confirmCallback, cancelCallback) => {
	return baseAlert(
		props,
		confirmCallback,
		cancelCallback,
		<ConfirmActionButton label="Aceptar" />,
		<CancelActionButton label="Cancelar" />,
		{ showCancelButton: !!cancelCallback },
		'confirm'
	);
};

export { alert, successAlert, confirmationAlert, deleteAlert, errorAlert };
